import { useCallback, useEffect, useState } from "react";
import { money } from "@/lib/format";
import { PHONE_HINT, PROVIDERS, fmtPhone, ghPhone, guessProvider, isOpen, openCheckout, providerLabel, type Provider } from "@/lib/paystack";
import { loadPublic, payCard, payMomo, payOtp, type PublicLink } from "@/lib/paylink";
import { Brand } from "./Brand";
import { cx } from "./ui";

type Step = "choose" | "momo" | "card";
const LOCK = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><rect x="5" y="11" width="14" height="10" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;
const mono = "font-mono";

/**
 * The customer's payment page for a payment link: the store's own design, Paystack underneath.
 * Mobile Money is a prompt to their phone (and the SMS code if Paystack asks for one); card
 * opens Paystack's secure card window, since card numbers must never touch our page.
 * `embedded` drops the header and footer, for showing it inside the customer display.
 */
export function PayLinkFlow({ code, embedded = false }: { code: string; embedded?: boolean }) {
  const [link, setLink] = useState<PublicLink | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  const [step, setStep] = useState<Step | null>(null);
  const [busy, setBusy] = useState(false);
  // "Wrong number?" leaves the waiting screen even while that request is still open.
  const [skip, setSkip] = useState(false);

  const load = useCallback(async () => {
    try {
      setLink(await loadPublic(code));
    } catch (e) {
      const m = e instanceof Error ? e.message : "Couldn't load the payment";
      if (!link) setGone(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
  useEffect(() => void load(), [load]);
  // Keep watching while it can still be paid: a MoMo approval or the card window confirms here.
  useEffect(() => {
    if (!link || link.status !== "open") return;
    const h = setInterval(() => void load(), 4000);
    return () => clearInterval(h);
  }, [link, load]);

  const run = async (f: () => Promise<PublicLink>) => {
    setBusy(true);
    setErr(null);
    try {
      const next = await f();
      setLink(next);
      return next;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That didn't work. Try again");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const cardNow = async () => {
    const next = await run(() => payCard(code));
    const ac = next?.attempt?.accessCode;
    if (!ac) return;
    try {
      await openCheckout(ac);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Paystack didn't open. Try again");
    }
    void load();
  };

  if (gone) return <Shell embedded={embedded}><Notice title="Payment link not found" text={gone} /></Shell>;
  if (!link) return <Shell embedded={embedded}><div className="p-8 text-center text-muted">Loading…</div></Shell>;

  const a = link.attempt;
  const current: Step = step ?? (link.method === "momo" ? "momo" : link.method === "card" ? "card" : "choose");
  const waitingMomo = !skip && a && a.channel === "prompt" && isOpen(a.status);

  let body;
  if (link.status === "paid") body = <Paid link={link} />;
  else if (link.status === "cancelled") body = <Notice title="This link was cancelled" text="Nothing was taken. Ask the cashier for a new link, or pay at the counter." />;
  else if (link.status === "expired") body = <Notice title="This link has expired" text="Nothing was taken. Ask the cashier to send a new link." />;
  else if (waitingMomo) body = <Approve link={link} busy={busy} onOtp={(otp) => void run(() => payOtp(code, otp))} onChange={() => { setStep("momo"); setSkip(true); }} />;
  else
    body = (
      <>
        <AmountBar label={current === "momo" ? "MOBILE MONEY" : current === "card" ? "CARD" : "TO PAY"} amount={link.amount} note={current === "choose" ? `${link.lines.reduce((n, l) => n + l.qty, 0)} items · ${link.store.name}` : undefined} />
        {current !== "momo" ? <Order link={link} /> : null}
        <main className="flex flex-col gap-3.5 p-5">
          {a && (a.status === "failed" || a.status === "abandoned") ? <p role="alert" className="border-2 border-[#b4231f] bg-white px-3 py-2.5 text-sm font-semibold text-[#8f1c19]">{a.message || "That payment didn't go through. Nothing was taken."} Try again.</p> : null}
          {current === "choose" ? (
            <>
              <h1 className="mb-1 text-[22px] font-black">{link.customer ? `${link.customer}, how` : "How"} would you like to pay?</h1>
              <Choice dark title="Mobile Money" sub="MTN, Telecel, AirtelTigo" icon="M6 2h12v20H6zM11 18h2" onClick={() => setStep("momo")} />
              <Choice title="Card" sub="Visa, Mastercard, Verve" icon="M2 5h20v14H2zM2 10h20M6 15h4" onClick={() => setStep("card")} />
              <p className="mt-1 text-[12.5px] text-muted">The cashier sees it the moment it's paid.</p>
            </>
          ) : current === "momo" ? (
            <MomoForm busy={busy} onSend={(phone, provider) => void run(() => payMomo(code, phone, provider)).then((r) => r && setSkip(false))} />
          ) : (
            <>
              <div className="flex flex-col gap-2.5 border border-line bg-white p-4">
                <b className="text-[17px]">Pay by card</b>
                <p className="text-sm leading-relaxed text-[#3a3935]">Your card number goes straight to Paystack's secure window. {link.store.name} never sees it.</p>
                <div className="flex gap-1.5">
                  {["VISA", "Mastercard", "Verve"].map((c) => <span key={c} className="border border-line px-2 py-1 text-xs font-extrabold">{c}</span>)}
                </div>
              </div>
              <button type="button" disabled={busy} onClick={() => void cardNow()} className="flex h-[60px] items-center justify-between bg-brand px-5 text-lg font-black text-white hover:bg-brand-dark disabled:opacity-50">
                <span>{busy ? "Opening…" : a && a.channel === "screen" && isOpen(a.status) ? "Open the card window again" : "Enter card details"}</span>
                <span className={cx(mono, "text-[15px]")}>{money(link.amount)}</span>
              </button>
            </>
          )}
          {err ? <p role="alert" className="text-sm font-semibold text-[#8f1c19]">{err}</p> : null}
          {current === "momo" ? <Link onClick={() => setStep("card")}>Pay by card instead</Link> : current === "card" ? <Link onClick={() => setStep("momo")}>Pay with Mobile Money instead</Link> : null}
        </main>
      </>
    );

  return <Shell embedded={embedded}>{body}</Shell>;
}

function Shell({ embedded, children }: { embedded: boolean; children: React.ReactNode }) {
  if (embedded) return <div className="flex flex-col bg-paper text-ink">{children}</div>;
  return (
    <div className="flex min-h-dvh justify-center bg-[#e7e5df]">
      <div className="flex w-full max-w-[460px] flex-col bg-paper text-ink">
        <header className="flex items-center justify-between bg-ink px-5 py-4 text-white">
          <Brand onDark sub="Secure payment" />
          <span className="flex items-center gap-1.5 text-xs text-white/70">{LOCK} Secure</span>
        </header>
        {children}
        <footer className="mt-auto flex items-center justify-end gap-1.5 border-t border-line px-5 py-4 text-[11.5px] text-muted">{LOCK} Paid through Paystack</footer>
      </div>
    </div>
  );
}

function AmountBar({ label, amount, note }: { label: string; amount: number; note?: string }) {
  return (
    <section className="flex flex-col gap-1 bg-brand px-5 py-4 text-white">
      <span className={cx(mono, "text-[11.5px] tracking-[0.14em] text-white/75")}>{label}</span>
      <span className={cx(mono, "text-4xl font-semibold leading-tight")}>{money(amount)}</span>
      {note ? <span className="text-[13px] text-white/80">{note}</span> : null}
    </section>
  );
}

function Order({ link }: { link: PublicLink }) {
  return (
    <section className="flex max-h-48 flex-col gap-2 overflow-y-auto border-b border-line bg-white px-5 py-3.5">
      {link.lines.map((l, i) => (
        <div key={i} className="flex justify-between gap-3 text-[14.5px]">
          <span>{l.name}{l.qty > 1 ? ` × ${l.qty}` : ""}</span>
          <span className={mono}>{money(l.total)}</span>
        </div>
      ))}
    </section>
  );
}

function Choice({ title, sub, icon, onClick, dark }: { title: string; sub: string; icon: string; onClick: () => void; dark?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={cx("flex min-h-[76px] items-center gap-3.5 border-2 px-4 py-3 text-left", dark ? "border-ink bg-ink text-white" : "border-line bg-white hover:border-ink")}>
      <span className="grid h-11 w-11 flex-none place-items-center bg-brand text-white">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d={icon} /></svg>
      </span>
      <span className="flex flex-1 flex-col gap-0.5">
        <b className="text-[17px]">{title}</b>
        <span className={cx("text-[13px]", dark ? "text-white/70" : "text-muted")}>{sub}</span>
      </span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}

function Link({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className="self-start py-1 text-sm font-bold text-brand underline underline-offset-4">{children}</button>;
}

function MomoForm({ busy, onSend }: { busy: boolean; onSend: (phone: string, provider: Provider) => void }) {
  const [raw, setRaw] = useState("");
  const [net, setNet] = useState<Provider | null>(null);
  const ph = ghPhone(raw);
  const guessed = ph.ok ? guessProvider(ph.local) : null;
  const provider = net ?? guessed;
  return (
    <>
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-bold">Your Mobile Money number</span>
        <input inputMode="tel" autoComplete="tel" autoFocus value={raw} onChange={(e) => { setRaw(ghPhone(e.target.value).keep); setNet(null); }} placeholder="024 000 0000" className="h-[60px] border-2 border-ink bg-white px-4 font-mono text-2xl font-semibold outline-none" />
        <span className={cx("text-xs", raw && !ph.ok ? "font-semibold text-[#8a4b00]" : "text-muted")}>{raw && !ph.ok ? PHONE_HINT : ph.ok ? fmtPhone(ph.local) : "0XX XXX XXXX, or start with 233"}</span>
      </label>
      <div role="radiogroup" aria-label="Network" className="grid grid-cols-3 gap-1.5">
        {PROVIDERS.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={provider === p.id} onClick={() => setNet(p.id)} className={cx("h-12 border-2 text-sm font-extrabold", provider === p.id ? "border-ink bg-ink text-white" : "border-line bg-white")}>
            {p.label}{provider === p.id ? " ✓" : ""}
          </button>
        ))}
      </div>
      {guessed && !net ? <p className="text-[12.5px] text-muted">Network picked from your number. Tap another if it's wrong.</p> : null}
      <button type="button" disabled={busy || !ph.ok || !provider} onClick={() => provider && onSend(ph.local, provider)} className="flex h-[60px] items-center justify-between bg-brand px-5 text-lg font-black text-white hover:bg-brand-dark disabled:opacity-45">
        <span>{busy ? "Sending…" : "Send payment request"}</span>
      </button>
    </>
  );
}

function Approve({ link, busy, onOtp, onChange }: { link: PublicLink; busy: boolean; onOtp: (otp: string) => void; onChange: () => void }) {
  const a = link.attempt!;
  const [otp, setOtp] = useState("");
  const code = a.status === "send_otp";
  return (
    <>
      <AmountBar label={`MOBILE MONEY${a.phone ? ` · ${a.phone}` : ""}`} amount={link.amount} note={providerLabel(a.provider) || undefined} />
      <main className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-black leading-tight">{code ? "Type the code you received" : "Check your phone"}</h1>
          <p className="text-[15px] leading-relaxed text-[#3a3935]">
            {code ? a.message || "A code was sent to your phone by SMS." : <>A prompt from {providerLabel(a.provider) || "your network"} is on its way. Enter your <b>MoMo PIN</b> there to approve {money(link.amount)}.</>}
          </p>
        </div>
        <ol className="flex flex-col border border-line bg-white">
          <Row state="done">Request sent</Row>
          <Row state="now">{code ? "Waiting for the code" : "Waiting for you to approve"}</Row>
          <Row state="later">Paid</Row>
        </ol>
        {code ? (
          <div className="flex flex-col gap-2.5 border-2 border-ink bg-white p-3.5">
            <div className="flex gap-2">
              <input inputMode="numeric" autoFocus aria-label="One-time code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="______" className="h-[52px] min-w-0 flex-1 border border-line-2 px-3.5 font-mono text-[22px] tracking-[0.3em] outline-none focus:border-ink" />
              <button type="button" disabled={busy || otp.length < 4} onClick={() => onOtp(otp)} className="h-[52px] bg-ink px-5 font-extrabold text-white disabled:opacity-45">Confirm</button>
            </div>
          </div>
        ) : null}
        <p className="text-[12.5px] text-muted">Never share your PIN with anyone, including the shop.</p>
        <Link onClick={onChange}>Wrong number? Change it</Link>
      </main>
    </>
  );
}

function Row({ state, children }: { state: "done" | "now" | "later"; children: React.ReactNode }) {
  return (
    <li className={cx("flex items-center gap-3 border-b border-line px-3.5 py-3 text-sm last:border-b-0", state === "now" && "font-bold", state === "later" && "text-[#8a8880]")}>
      {state === "done" ? (
        <span className="grid h-[22px] w-[22px] place-items-center bg-brand text-xs font-extrabold text-white">✓</span>
      ) : state === "now" ? (
        <span className="grid h-[22px] w-[22px] place-items-center bg-warn"><span className="h-2 w-2 animate-pulse bg-ink" /></span>
      ) : (
        <span className="h-[22px] w-[22px] border border-line-2" />
      )}
      {children}
    </li>
  );
}

function Paid({ link }: { link: PublicLink }) {
  return (
    <>
      <section className="flex flex-col items-start gap-2.5 bg-brand px-5 py-7 text-white">
        <span className="grid h-[52px] w-[52px] place-items-center bg-white text-brand">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
        </span>
        <h1 className="text-[28px] font-black">Paid. Thank you!</h1>
        <span className={cx(mono, "text-[15px]")}>{money(link.amount)}{link.attempt?.paidWith ? ` · ${link.attempt.paidWith}` : ""}</span>
      </section>
      <main className="flex flex-col gap-3.5 p-5">
        <p className="text-[15px] leading-relaxed">The cashier can see it's paid. Your receipt comes from the till once the sale is complete.</p>
      </main>
    </>
  );
}

function Notice({ title, text }: { title: string; text: string }) {
  return (
    <main className="flex flex-col gap-2 p-6">
      <h1 className="text-2xl font-black">{title}</h1>
      <p className="text-[15px] leading-relaxed text-[#3a3935]">{text}</p>
    </main>
  );
}
